import React from "react";
import { AppContext } from "../../contexts/AppContext";
import Card from "../../components/page/Card";
import GerenciadorDataTable from "./GerenciadorInvoice/GerenciadorDataTable";
import { primeiroDiaDoMes, ultimoDiaDoMes } from "../../helpers/mesesHelper";
import GerenciadorFiltros from "./GerenciadorInvoice/GerenciadorFiltros";
import EnviarEmailModal from "../notasFiscais/GerenciadorNFSe/EnviarEmailModal";
import VisualizarNFSeModal from "../notasFiscais/GerenciadorNFSe/VisualizarNFSeModal";
import useApi from "../../hooks/useApi";
import InvoiceApiClient from "../../api/invoices";
import { debounce } from "lodash";
import CommonsApiClient from "../../api/commons";
import Validation from "../../helpers/Validation";
import ErrorSwal from "../../components/Swal/errorSwal";
import CommonSwal from "../../components/Swal/commonSwal";
import OptionsSwal from "../../components/Swal/optionsSwal";

function GerenciadorInvoices() {
  const [filtros, setFiltros] = React.useState({
    inicio: primeiroDiaDoMes,
    fim: ultimoDiaDoMes,
  });
  const [linhas, setLinhas] = React.useState([]);
  const [enviarEmailModal, setEnviarEmailModal] = React.useState(false);
  const [values, setValues] = React.useState({});
  const [visualizarDocumentoModal, setVisualizarDocumentoModal] =
    React.useState(false);
  const { session } = React.useContext(AppContext);

  const getInvoicesCb = React.useCallback(
    (inicio = null, fim = null) => {
      return InvoiceApiClient.listInvoices(
        session?.empresa.replace(/E/gi, ""),
        {
          data_inicial: inicio ? inicio : filtros.inicio,
          data_final: fim ? fim : filtros.fim,
        }
      );
    },
    [filtros.fim, filtros.inicio, session?.empresa]
  );

  const enviarEmailsApi = useApi(InvoiceApiClient.enviarEmails);
  const deleteInvoicecalApi = useApi(InvoiceApiClient.deleteInvoice);
  const { request: atualizarInvoices, data: invoicesResponse } =
    useApi(getInvoicesCb);
  const documentoRouteApi = useApi(CommonsApiClient.documentoRoute);

  const getInvoices = React.useMemo(
    () =>
      debounce(() => {
        atualizarInvoices();
      }, 600),
    [atualizarInvoices]
  );

  //Busca notas fiscais ao renderizar tela
  React.useEffect(() => {
    if (session?.empresa) {
      getInvoices();

      return () => {
        getInvoices.cancel();
      };
    }
  }, [getInvoices, session?.empresa]);

  //Define o retorno da api nos valores das linhas da tabela
  React.useEffect(() => {
    if (invoicesResponse) setLinhas(invoicesResponse);
  }, [invoicesResponse]);

  const downloadInvoice = async (caminho, id) => {
    const response = await documentoRouteApi.request(caminho, {
      ext: "pdf",
      type: "download",
    });
    if (!response) return;
    const blob = new Blob([response]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `invoice-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const togVisualizarModal = async (caminho) => {
    const response = await documentoRouteApi.request(caminho, {
      ext: "pdf",
      type: "visualizar",
    });
    if (!response) return;
    const pdfBlob = new Blob([response], { type: "application/pdf" });
    const documentUrl = URL.createObjectURL(pdfBlob);
    setValues({ documento: documentUrl });
    setVisualizarDocumentoModal(true);
  };

  const togEnviarEmailModal = (uid, eno) => {
    setValues({
      ...values,
      uid: uid,
      eno: eno === "ENO",
      pdf: true,
    });
    setEnviarEmailModal(true);
  };

  const enviarEmails = async () => {
    if (!values.emails)
      return ErrorSwal({
        text: "Nenhum email informado.",
      });

    var emailsArray = values.emails.replace(/ /g, "");
    emailsArray = emailsArray.split(",");

    const formData = new FormData();
    formData.append("uid", values.uid);
    formData.append("emails", JSON.stringify(emailsArray));
    formData.append("pdf", 1);

    const emails = values.emails;
    const erros = Validation.validarEmails(emails);
    if (erros)
      return ErrorSwal({
        text: "Por favor, forneça email(s) válido(s).",
      });

    const response = await enviarEmailsApi.request(
      session.empresa.replace(/E/gi, ""),
      formData
    );
    if (!response) return;
    CommonSwal({
      title: "E-mail enviado com sucesso!",
      icon: "success",
    }).then(async () => {
      setEnviarEmailModal(false);
      await atualizarInvoices();
    });
  };

  const deleteInvoice = (uid) => {
    OptionsSwal({
      title: "Tem certeza que deseja excluir este arquivo?",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await deleteInvoicecalApi.request(
          session?.empresa.replace(/E/gi, ""),
          uid
        );
        if (!response) return;
        CommonSwal({
          icon: "success",
          title: "Invoice excluído com sucesso!",
        }).then(async () => {
          await atualizarInvoices();
        });
      }
    });
  };

  return (
    <>
      <Card>
        <GerenciadorFiltros filtrosHook={{ filtros, setFiltros }} />
        <GerenciadorDataTable
          linhas={linhas}
          downloadInvoice={downloadInvoice}
          togVisualizarModal={togVisualizarModal}
          togEnviarEmailModal={togEnviarEmailModal}
          deleteInvoice={deleteInvoice}
        />
      </Card>
      <VisualizarNFSeModal
        visualizarModalHook={{
          visualizarDocumentoModal,
          setVisualizarDocumentoModal,
        }}
        documento={values.documento}
      />
      <EnviarEmailModal
        modalHook={{ enviarEmailModal, setEnviarEmailModal }}
        valuesHook={{ values, setValues }}
        enviarEmails={enviarEmails}
      />
    </>
  );
}

export default GerenciadorInvoices;
